import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Aviso, Botao, CampoDeSelecao, Cartao, Modal } from '../../componentes/ui';
import { useIdentidade } from '../../hooks/useSessao';
import { requisicaoAutenticada } from '../../servicos/http';
import { ErroDaApi } from '../ErroDaApi';
import { rotaDaEmpresa } from '../origem-da-empresa';
import type { Settings } from './ConfiguracaoDeCredito';

interface Parte {
  readonly businessId: string;
  readonly razaoSocial: string;
}

/**
 * RN-621 — O assessor responsável pelo cliente.
 *
 * ## A regra
 *
 * Todo cliente cadastrado por um assessor fica vinculado a ele — é por esse
 * vínculo que o assessor vê e gerencia o cliente antes de qualquer contrato.
 * Trocar é decisão da operação: só o admin vê o seletor; os demais leem.
 *
 * ## Onde mora
 *
 * Na aba Serviços, junto da configuração de papéis, porque é dado da
 * operação (quem intermedeia), não do cadastro (endereço, conta). O cabeçalho
 * do perfil repete o nome para quem só quer saber "de quem é este cliente".
 */
export function AssessorDoCliente(props: {
  readonly businessId: string;
  readonly settings: Settings;
}): JSX.Element {
  const identidade = useIdentidade();
  const queryClient = useQueryClient();
  const admin = identidade?.isAdmin === true;
  const atual = props.settings.assessor;
  const [escolhido, setEscolhido] = useState<string | null>(null);
  // A troca é ato deliberado, não um seletor aberto no meio do cartão: um
  // clique errado no dropdown trocava quem responde pelo cliente.
  const [trocando, setTrocando] = useState(false);

  const assessores = useQuery({
    queryKey: ['carteira', 'assessores'],
    queryFn: () => requisicaoAutenticada<Parte[]>('/icms/advisors'),
    enabled: admin,
  });

  const definir = useMutation({
    mutationFn: (assessorId: string | null) =>
      requisicaoAutenticada<Settings>(`/icms/clients/${props.businessId}/assessor`, {
        metodo: 'PUT',
        corpo: { assessorId },
      }),
    onSuccess: (dados) => {
      queryClient.setQueryData(['settings', props.businessId], dados);
      void queryClient.invalidateQueries({ queryKey: ['cliente', props.businessId] });
      void queryClient.invalidateQueries({ queryKey: ['carteira'] });
      setEscolhido(null);
      setTrocando(false);
    },
  });

  const valor = escolhido ?? atual?.businessId ?? '';
  const mudou = valor !== (atual?.businessId ?? '');

  const fecharTroca = (): void => {
    definir.reset();
    setEscolhido(null);
    setTrocando(false);
  };

  return (
    <Cartao
      como="section"
      titulo="Assessor responsável"
      subtitulo="Quem cadastrou e assessora este cliente. É por este vínculo que o assessor enxerga o cliente antes de haver contrato."
      acao={
        admin ? (
          <Botao variante="secundario" tamanho="pequeno" onClick={() => setTrocando(true)}>
            Trocar assessor
          </Botao>
        ) : undefined
      }
    >
      <div className="flex flex-col gap-4">
        {atual === null ? (
          <p className="text-sm text-conteudo-sutil">
            Sem assessor definido — a operadora assessora este cliente diretamente.
          </p>
        ) : (
          <p className="text-sm text-conteudo-primario">
            <Link
              to={rotaDaEmpresa(atual.businessId, 'assessores')}
              className="underline-offset-4 hover:text-acento-ciano hover:underline"
            >
              {atual.razaoSocial}
            </Link>
          </p>
        )}

        {!admin && (
          <Aviso tom="informacao">Só a administração da MS3 troca o assessor de um cliente.</Aviso>
        )}
      </div>

      <Modal
        aberto={trocando}
        aoFechar={fecharTroca}
        titulo="Trocar assessor responsável"
        descricao={
          atual === null
            ? 'Hoje quem assessora este cliente é a operadora.'
            : `Hoje quem assessora este cliente é ${atual.razaoSocial}.`
        }
        rodape={
          <>
            <Botao variante="secundario" onClick={fecharTroca}>
              Cancelar
            </Botao>
            <Botao
              disabled={!mudou}
              carregando={definir.isPending}
              onClick={() => definir.mutate(valor === '' ? null : valor)}
            >
              Salvar assessor
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <CampoDeSelecao
            rotulo="Novo assessor"
            value={valor}
            onChange={(e) => setEscolhido(e.target.value)}
            opcoes={[
              { valor: '', rotulo: 'Sem assessor (operadora)' },
              ...(assessores.data ?? []).map((a) => ({
                valor: a.businessId,
                rotulo: a.razaoSocial,
              })),
            ]}
          />

          <Aviso tom="informacao">
            O assessor novo passa a enxergar este cliente; o anterior deixa de vê-lo, a menos que
            esteja num contrato do ano. Contratos já fechados não mudam de assessor.
          </Aviso>

          {definir.isError && (
            <ErroDaApi titulo="Não foi possível trocar o assessor" erro={definir.error} />
          )}
        </div>
      </Modal>
    </Cartao>
  );
}
